export type OTPStatus = 'default' | 'error' | 'verifying' | 'expired'

// `reason` on `confirmed` lets the renderer decide whether mind-change
// (yes → cant-go) is reversible. `free` is reversible; `paid` is not
// (would require a refund — see Gap 6 in the audit doc).
export type View =
  | { kind: 'sign-in'; phone: string; error: string | undefined; loading: boolean }
  | { kind: 'otp'; phone: string; value: string; status: OTPStatus }
  | { kind: 'rsvp' }
  | { kind: 'payment-loading'; error: string | undefined }
  | { kind: 'payment'; clientSecret: string; error: string | undefined; loading: boolean }
  | { kind: 'processing' }
  | { kind: 'confirmed'; reason: 'paid' | 'free' }

type PaymentStatus = 'paid' | 'processing' | 'pending' | null | undefined
type RSVPStatus = 'unanswered' | 'confirmed' | 'cant-go'

// `paymentLocalStatus` is the client-side mirror of the Stripe Elements
// result. When Stripe says succeeded/processing, the server takes a beat
// to catch up via webhook → event_payments → next gathering refetch.
// Treating the local result as effective `my_payment_status` lets us
// route to confirmed/processing immediately, eliminating the "I paid
// but the card won't dismiss" gap.
type PaymentLocalStatus = 'succeeded' | 'processing' | null

export interface DeriveInputs {
  authenticated: boolean
  rsvpStatus: RSVPStatus
  myPaymentStatus: PaymentStatus
  eventPrice: number | null | undefined
  paymentClientSecret: string | null
  paymentError: string | null | undefined
  signIn: { phone: string; error: string | undefined; loading: boolean }
  otp: { active: boolean; phone: string; value: string; status: OTPStatus }
  paymentSubmitting: boolean
  paymentSubmitError: string | undefined
  paymentLocalStatus: PaymentLocalStatus
}

// Projects server + local UI inputs into the view the component should render.
// Every branch is total: there is no "previous state" to drift from, so the
// reducer's GAP A / B / C bugs cannot exist here.
export function deriveView(i: DeriveInputs): View {
  if (!i.authenticated) {
    return i.otp.active
      ? { kind: 'otp', phone: i.otp.phone, value: i.otp.value, status: i.otp.status }
      : {
          kind: 'sign-in',
          phone: i.signIn.phone,
          error: i.signIn.error,
          loading: i.signIn.loading,
        }
  }

  if (i.myPaymentStatus === 'paid' || i.paymentLocalStatus === 'succeeded') {
    return { kind: 'confirmed', reason: 'paid' }
  }
  if (i.myPaymentStatus === 'processing' || i.paymentLocalStatus === 'processing') {
    return { kind: 'processing' }
  }

  if (i.rsvpStatus !== 'confirmed') return { kind: 'rsvp' }

  const isFree = !i.eventPrice || i.eventPrice <= 0
  if (isFree) return { kind: 'confirmed', reason: 'free' }

  const error = i.paymentSubmitError ?? i.paymentError ?? undefined

  if (i.paymentClientSecret) {
    return {
      kind: 'payment',
      clientSecret: i.paymentClientSecret,
      error,
      loading: i.paymentSubmitting,
    }
  }

  return { kind: 'payment-loading', error }
}
